import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';
import { submitToPhp } from '../lib/phpApi';

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8001' : '');
      if (!backendUrl) {
        setError('Contact service is currently unavailable. Please try again later.');
        return;
      }
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Contact service is temporarily unavailable.');
      const result = await response.json();

      if (result.success) {
        setStatus('Thanks — your message has been sent successfully.');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        throw new Error(result.error || 'Failed to send message.');
      }
    } catch (err) {
      setError(err.message || 'Unable to submit the form.');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title="Contact | Infinite Reyn Productions - Get in Touch"
        description="Contact Infinite Reyn Productions for inquiries about our Books, Music, and Theatre projects. Reach out to discuss collaborations, press inquiries, or general questions."
        canonical="/contact"
        keywords="contact Infinite Reyn Productions, reach out, inquiries, press contact, collaboration opportunities, Evie Montclaire contact, Divine Reyn contact"
      />
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-8 left-8 z-50 flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="text-sm tracking-wider">BACK</span>
      </button>

      {/* Navigation Hint */}
      <div className="fixed top-8 right-8 z-50">
        <span className="text-xs tracking-[0.3em] text-gold">CONTACT</span>
      </div>

      <div className="max-w-3xl mx-auto px-8 py-32">
        {/* Header */}
        <div className="text-center mb-20">
          <h1 className="text-4xl md:text-5xl font-light mb-6 tracking-wide">
            Let's Talk
          </h1>
          <div className="w-24 h-px bg-gold mx-auto mb-8"></div>
          <p className="text-lg text-white/70 font-light leading-relaxed max-w-2xl mx-auto">
            Whether you have a question, a collaboration idea, or just want to say hello — we'd love to hear from you.
          </p>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Name Field */}
          <div className="group">
            <label 
              htmlFor="name" 
              className="block text-xs tracking-[0.2em] text-gold mb-3 uppercase"
            >
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/30 focus:border-gold focus:outline-none transition-colors font-light"
              placeholder="Your name"
            />
          </div>

          {/* Email Field */}
          <div className="group">
            <label 
              htmlFor="email" 
              className="block text-xs tracking-[0.2em] text-gold mb-3 uppercase"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/30 focus:border-gold focus:outline-none transition-colors font-light"
              placeholder="your@email.com"
            />
          </div>

          {/* Subject Field */}
          <div className="group">
            <label 
              htmlFor="subject" 
              className="block text-xs tracking-[0.2em] text-gold mb-3 uppercase"
            >
              Subject
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/30 focus:border-gold focus:outline-none transition-colors font-light"
              placeholder="What's this about?"
            />
          </div>

          {/* Message Field */}
          <div className="group">
            <label 
              htmlFor="message" 
              className="block text-xs tracking-[0.2em] text-gold mb-3 uppercase"
            >
              Message
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="6"
              className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/30 focus:border-gold focus:outline-none transition-colors font-light resize-none"
              placeholder="Tell us what's on your mind..."
            />
          </div>

          {/* Submit Button */}
          {status && (
            <p className="text-sm text-emerald-300 mb-4">{status}</p>
          )}
          {error && (
            <p className="text-sm text-rose-300 mb-4">{error}</p>
          )}
          <div className="pt-8 text-center">
            <button
              type="submit"
              className="inline-flex items-center justify-center px-12 py-4 border border-gold text-gold hover:bg-gold hover:text-black transition-all tracking-[0.2em] text-sm uppercase font-light group"
            >
              <span>Send Message</span>
              <svg 
                className="w-4 h-4 ml-3 group-hover:translate-x-1 transition-transform" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </form>

        {/* Alternative Contact Info */}
        <div className="mt-24 text-center space-y-6">
          <div className="w-16 h-px bg-white/20 mx-auto mb-8"></div>
          <p className="text-sm text-white/50 tracking-wider">
            OR REACH US DIRECTLY
          </p>
          <a 
            href="mailto:hello@infinitereyn.com" 
            className="block text-lg text-white/70 hover:text-gold transition-colors font-light"
          >
            hello@infinitereyn.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default Contact;
